import React, { useState, useEffect } from "react";
import { Col, Input, Row, Button, Form, Checkbox, Radio, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { createTemplate, updateTemplates } from "../redux/actions";
import AddFieldsComponent from "./addFieldsComponents";
import { UseTemplateFields } from "./useTemplateFields";
import { useNavigate } from "react-router-dom";
import { fetchAllProjects } from '../../ProjectSetup/redux/actions';

const TemplateDetail = ({ isTemplateUpdate, canUpdate }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [currentTemplate, setCurrentTemplate] = useState({});
  const [fieldCounter, setFieldCounter] = useState(0);
  const [loading, setLoading] = useState(false);
  const [turnaroundTime, setTurnaroundTime] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const { allProjects, fetchProjectsLoading } = useSelector((state) => state.projectsSetupReducer);
  const { createTemplateLoading, currentTemplateObj, updateLoading } =
    useSelector((state) => state.materialTemplatesReducer);
  const navigate = useNavigate();

  const [
    inputFields,
    setInputFields,
    createField,
    deleteField,
    updateField,
    onEditClick,
    createOption,
    updateOption,
    deleteOption,
    onEditClickOption,
    getFormattedFields
  ] = UseTemplateFields();

  let maskFieldPattern = /^[0-9][0-9]-[0-9][0-9]$/;

  const formItemLayout = {
    labelCol: {
      span: 24,
    },
    wrapperCol: {
      span: 24,
    },
  };

  useEffect(() => {
    dispatch(fetchAllProjects({
      page: 1,
      limit: 100,
      search: {},
      hasMaterialTemplate: false,
      showByDefault: true
    }));
  }, [dispatch]);

  useEffect(() => {
    setFieldCounter(0);
  }, []);

  const handleProjectChange = (value) => {
    setSelectedProject(value);
  };

  const onChangeTurnaroundTime = (e) => {
    setTurnaroundTime(e.target.checked);
  };

  const onFinish = (values) => {
    // Get the formatted fields from the hook
    const fields = getFormattedFields();
    
    // Ensure projectId is set from either form values or state
    const projectId = values.projectId || selectedProject;
    
    const newValues = {
      projectId: currentTemplateObj?.project ? currentTemplateObj?.project?.id : projectId,
      name: values.name,
      fields: fields,
    };

    if (isTemplateUpdate) {
      dispatch(
        updateTemplates({
          updateData: newValues,
          id: currentTemplateObj.id,
          navigate,
        })
      );
    } else {
      dispatch(createTemplate({ ...newValues, navigate }));
    }
  };

  useEffect(() => {
    if (currentTemplateObj && currentTemplateObj.id) {
      console.log("Processing currentTemplateObj:", currentTemplateObj);
      
      // Set turnaround time
      setTurnaroundTime(!!currentTemplateObj.turnaround_time);
      
      // Set both state and form values
      setSelectedProject(currentTemplateObj.projectId);
      form.setFieldsValue({
        projectId: currentTemplateObj?.project ? `${currentTemplateObj?.project?.projectNumber} - ${currentTemplateObj?.project?.client}` : 'Select Project',
        name: currentTemplateObj.name,
      });

      // Convert API fields format to our internal format
      const newFields = Array.isArray(currentTemplateObj.fields) ? currentTemplateObj.fields.map((field, fieldIndex) => ({
        id: `field_${Date.now()}_${fieldIndex}_${Math.random().toString(36).substr(2, 9)}`,
        name: field.name,
        field_type: field.type === 'selector' ? 'select_field' : 
                    field.type === 'text' ? 'text_field' : 
                    field.type === 'date' ? 'date_field' : 'text_field',
        options: field.type === 'selector' && Array.isArray(field.options) ? field.options.map((opt, optIndex) => ({
          id: `option_${Date.now()}_${fieldIndex}_${optIndex}_${Math.random().toString(36).substr(2, 9)}`,
          name: opt.name,
          isEdit: false,
          isLocal: false
        })) : [],
        isEdit: false,
        isLocal: false
      })) : [];

      console.log("Processed fields:", newFields);
      setInputFields(newFields);
      setCurrentTemplate(currentTemplateObj);
    }
  }, [currentTemplateObj]);

  useEffect(() => {
    setLoading(createTemplateLoading);
  }, [createTemplateLoading]);

  return (
    <>
      <Row>
        <Col span={24} className="heading-col">
          <Row align="middle" justify="space-between">
            <Col>
              <div>
                <span className="heading-span">
                  MATERIAL TEMPLATE {isTemplateUpdate ? " UPDATE" : " CREATION"}
                </span>
              </div>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Form
            {...formItemLayout}
            form={form}
            name="user-details"
            onFinish={onFinish}
            initialValues={currentTemplate}
            scrollToFirstError
            labelWrap={true}
            layout="horizontal"
            className="user-detail-form"
            requiredMark={false}
            disabled={createTemplateLoading}
          >
            <Col
              span={24}
              className={`user-details-section document-type-section`}
            >
              <Row justify="center">
                <Col md={22} lg={22} xl={20} xxl={16}>
                  <Row gutter={[20, 10]}>
                    <Col md={16} lg={14} xl={15} xxl={15}>
                      <Form.Item
                        name="projectId"
                        label="Project"
                        rules={[
                          {
                            required: true,
                            message: "Please select a project",
                          },
                        ]}
                      >
                        <Select
                          placeholder="Select a project"
                          onChange={handleProjectChange}
                          value={selectedProject}
                          style={{ width: '100%' }}
                          disabled={isTemplateUpdate || !canUpdate}
                        >
                          {fetchProjectsLoading ? (
                            <Select.Option key="loading" value="">
                              Loading projects...
                            </Select.Option>
                          ) : allProjects?.map((project) => (
                            <Select.Option key={project.id} value={project.id}>
                              {project.projectNumber} - {project.client}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[20, 10]}>
                    <Col md={16} lg={14} xl={15} xxl={15}>
                      <Form.Item
                        rules={[
                          {
                            required: true,
                            message: "Required!",
                          },
                        ]}
                        name="name"
                        label="Template Name"
                      >
                        <Input
                          disabled={!canUpdate}
                          readOnly={loading}
                          placeholder="Insert Template Name"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <hr />
                  <Col>
                    <AddFieldsComponent
                      inputFields={inputFields}
                      setInputFields={setInputFields}
                      createField={createField}
                      deleteField={deleteField}
                      updateField={updateField}
                      onEditClick={onEditClick}
                      createOption={createOption}
                      updateOption={updateOption}
                      deleteOption={deleteOption}
                      onEditClickOption={onEditClickOption}
                      canUpdate={canUpdate}
                    />
                  </Col>
                  {canUpdate && <hr />}

                </Col>
                {canUpdate && <Col span={23} className="heading-col"></Col>}
                <Col md={24} lg={23} xl={20} xxl={16}>
                  {canUpdate && (
                    <Row gutter={[20, 10]} justify="center">
                      <Row align="middle" justify="center">
                        <Col>
                          <Button
                            type="primary"
                            className="save-btn"
                            size="middle"
                            htmlType="submit"
                            loading={updateLoading || createTemplateLoading}
                          >
                            {isTemplateUpdate ? "Update" : "Save"} Template
                          </Button>
                        </Col>
                      </Row>
                    </Row>
                  )}
                </Col>
              </Row>
            </Col>
          </Form>
        </Col>
      </Row>
      <div className="empty-div"></div>
    </>
  );
};
export default TemplateDetail;
